<template>
  <div
    ref="slideShowWrapper"
    class="slideshow"
    tabindex="0"
    data-test="main_image_container"
    :data-orientation-id="selectedType?.id"
    :data-orientation-name="selectedType?.name"
  >
    <div class="focus-border" />
    <Slideshow
      ref="slideShow"
      :list="carouselImages"
      :is-zoomed-in="isZoomedIn"
      :initial-index="currentPage"
      :first-alt-dot="firstAltDot"
      autoplay-mode="manual"
      :in-overlay="inOverlay"
      :show-indicator="showIndicator"
      :threshold-limit="0.8"
      @slideshow-change="slideShowChangeHandler"
      @update-first-alt-dot="updateFirstAltDotHandler"
    >
      <template #item="slotProps">
        <div
          v-if="slotProps.data.video"
          class="product-video-wrapper"
        >
          <ProductVideo
            :video-id="videoId"
            :is-video-selected="isVideoSelected"
          />
        </div>
        <div
          v-else
          class="product-image-wrapper"
          @click="emit('imageClick')"
        >
          <div class="image-layer" />
          <Picture
            :image-url="carouselImages[slotProps.index].path"
            :alt="imageAlt"
            :image-sizes-by-breakpoints="imageSizesByBreakpoints"
            :fetch-priority="slotProps.index === 0 || slotProps.index === 1 || slotProps.index === carouselImages.length - 1"
            :lazy-load="slotProps.index !== 0 && slotProps.index !== 1 && slotProps.index !== carouselImages.length - 1"
            :image-class="slotProps.index === 0 ? 'stylitics-shop-similar' : ''"
            draggable="false"
          />
          <transition name="fade">
            <JumboSwatch v-if="carouselImages[slotProps.index].showJumboSwatch && !inOverlay" />
          </transition>
        </div>
      </template>
    </Slideshow>
  </div>
</template>

<script setup>
import { useFocusWithin, onKeyStroke } from '@vueuse/core';
import {
  ref, computed, watch, onMounted,
} from 'vue';
import { useStore } from 'vuex';
import { useType } from '@modules/types';

import Picture from '@components/common/Media/Picture';
import Slideshow from '@components/common/Media/SlideShow';
import JumboSwatch from '@components/JumboSwatch.vue';
import ProductVideo from '@components/ProductVideo.vue';

import { useImages } from '@composables/useImages';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useOutfittingShopSimilar } from '@composables/useOutfittingShopSimilar';

const { start } = useOutfittingShopSimilar();

const imageSizesByBreakpoints = {
  small: 500,
  medium: 500,
  large: 500,
  mlarge: 700,
  xlarge: 700,
};

const props = defineProps({
  currentPage: {
    /** @type {import('vue').PropType<number>} */
    type: Number,
    default: 1,
  },
  firstAltDot: {
    /** @type {import('vue').PropType<number>} */
    type: Number,
    default: 1,
  },
  currentPageOnHover: {
    /** @type {import('vue').PropType<number | null>} */
    type: Number || null,
    default: null,
  },
  inOverlay: {
    /** @type {import('vue').PropType<boolean>} */
    type: Boolean,
    default: false,
  },
  showShopSimilar: {
    /** @type {import('vue').PropType<boolean>} */
    type: Boolean,
    default: false,
  },
  isZoomedIn: {
    /** @type {import('vue').PropType<boolean>} */
    type: Boolean,
    default: false,
  },
  carouselImages: {
    /** @type {import('vue').PropType<Image[]>} */
    type: Array,
    require: true,
    default: () => [],
  },
  showIndicator: {
    /** @type {import('vue').PropType<boolean>} */
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['updateCarousel', 'imageClick', 'updateFirstAltDot']);

const store = useStore();
const slideShow = ref(null);
const slideShowWrapper = ref(null);
const { focused } = useFocusWithin(slideShowWrapper);

const { productId } = store.state.pageData;

const { selectedType } = useType(productId);
const { videoId, videoIndex } = useImages();

const isVideoSelected = computed(() => videoIndex.value === props.currentPageOnHover || (videoIndex.value === props.currentPage - 1));

const imageAlt = computed(() => {
  const { brandName, pdfEmailDescription } = useCommonProductGetters({ productId });
  return pdfEmailDescription.value ? `${brandName.value} - ${pdfEmailDescription.value}` : brandName.value;
});

onMounted(() => {
  if (props.showShopSimilar) start();
});

onKeyStroke(['ArrowLeft'], () => {
  if (focused.value) slideShow.value.move(-1);
});
onKeyStroke(['ArrowRight'], () => {
  if (focused.value) slideShow.value.move(1);
});

/**
 * @param {Image[]} a
 * @param {Image[]} b
 */
const imagesAreEqual = (a, b) => {
  if (a.length !== b.length) return false;
  return a.findIndex((img, i) => img.filePath !== b[i].filePath) === -1;
};

watch(
  [() => props.currentPage, () => props.currentPageOnHover, () => props.carouselImages, () => props.firstAltDot],
  ([currentPage, currentPageOnHover, carouselImages, firstAltDot],
    oldProps) => {
    if (currentPageOnHover !== null) slideShow.value.change(currentPageOnHover + 1);
    else {
      slideShow.value.change(currentPage);
      emit('updateCarousel', currentPage);
      emit('updateFirstAltDot', firstAltDot);
    }
    if (!imagesAreEqual(carouselImages, oldProps[2])) emit('updateCarousel', 1);
  },
);

const pauseVideo = () => {
  if (videoId) {
    const videoPlayer = window?.videojs?.players[`video_${videoId}`];
    if (videoPlayer) {
      setImmediate(() => {
        videoPlayer.pause();
      });
    }
  }
};
/**
 * @param {number} index
 */
const slideShowChangeHandler = (index) => {
  if (props.currentPageOnHover === null) emit('updateCarousel', index);
  pauseVideo();
};
/**
 * @param {number} value
 */
const updateFirstAltDotHandler = (value) => {
  emit('updateFirstAltDot', value);
};
</script>

<style lang="scss" scoped>
  .product-image-wrapper {
    position: relative;
    cursor: zoom-in;
    height: 100%;
    .image-layer {
      position: absolute;
      z-index: 1;
      width: 100%;
      height: 100%;
      user-select: none;
      -webkit-user-drag: none;
      display: none;

      @include breakpoint(large) {
        display: block;
      }
      img {
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -o-user-select: none;
        user-select: none;
      }
    }
  }

  .product-video-wrapper {
    height: 100%;
  }
  .focus-border {
    position: absolute;
    width: 100%;
    height: 100%;
    transition: 0.2s ease;
    opacity: 0;
    border: 2px solid rgb(0, 95, 204);
    z-index: 1;
    visibility: hidden;
    pointer-events: none;
  }
  .slideshow:focus-visible {
    .focus-border {
      @include breakpoint(medium up) {
        visibility: visible;
        opacity: 1;
      }
    }
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease-in-out;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
